import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { JournalSummary } from "../../../../shared/work-journal";
import type { WorkflowSectionContext, WorkflowSectionView } from "../types";
import { conversationKey, useConversationPanel } from "../panel";
import { askAbout, readJournal, readJournalImage, toTimeline } from "./api";
import { JournalPanel } from "./JournalPanel";

/** How often an open panel reads again while a run is writing to the journal. */
const LIVE_POLL_MS = 3000;

/**
 * The thread's Work Journal in the side panel.
 *
 * Read when a conversation opens and again when a turn ends. During a run it
 * reads every few seconds while the panel is open, so steps appear as they
 * happen, and, closed, until the first step is written, so the rail button
 * shows with the run's first step rather than at its end.
 * Never opens on its own: the transcript already shows the live steps.
 *
 * Like the end report, everything shown is keyed to the conversation it was
 * read for, so switching conversations never shows the old one's journal.
 */
export function useWorkJournalView(ctx: WorkflowSectionContext): WorkflowSectionView {
  const { t } = useLocale();
  const { busy } = ctx;
  const { token, orgId, projectId, userId, threadId } = ctx.scope;
  const key = conversationKey(ctx.scope);
  useConversationPanel(ctx);
  const [loaded, setLoaded] = useState<{ key: string; list: JournalSummary[] }>({ key: "", list: [] });
  const [tick, setTick] = useState(0);

  const entries = loaded.key === key ? loaded.list : [];
  const open = ctx.panelOpen;
  const workflowId = ctx.workflowId;
  const ready = ctx.enabled && token && orgId && projectId && userId && threadId && workflowId
    ? { token, orgId, projectId, userId, threadId, workflowId } : null;

  useEffect(() => {
    // During a run a closed panel reads only until the first step shows; the read after the run covers the rest.
    if (!ready || (busy && !open && entries.length > 0)) return;
    const controller = new AbortController();
    readJournal(ready, controller.signal)
      .then((list) => setLoaded({ key, list }))
      .catch(() => { if (!controller.signal.aborted) setLoaded((prev) => prev.key === key ? prev : { key, list: [] }); });
    return () => controller.abort();
    // `ready` is rebuilt every render; `key` and `token` stand for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.enabled, token, key, workflowId, busy, open, tick, entries.length > 0]);

  const waiting = busy && !open && entries.length === 0;
  useEffect(() => {
    if (!(open || waiting) || !busy) return;
    const timer = window.setInterval(() => setTick((n) => n + 1), LIVE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, waiting, busy]);

  const groups = useMemo(() => toTimeline(entries, busy), [entries, busy]);
  const loadImage = useCallback((path: string, signal: AbortSignal) => ready
    ? readJournalImage(ready, path, signal)
    : Promise.reject(new Error("No conversation is open.")),
  // `ready` is rebuilt every render; `key`, `token` and whether it exists at all stand for it.
  // The last matters: the panel can turn on after the conversation opened (a workflow's view
  // is recognised once its settings are read), and a loader made before then only rejects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [token, key, ready !== null]);

  return {
    rail: ready && (entries.length || open) ? {
      label: t("workJournal.title"),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 6h4M2 10h4M2 14h4M2 18h4" />
          <rect width="16" height="20" x="4" y="2" rx="2" />
          <path d="M9.5 8h5M9.5 12H16M9.5 16H14" />
        </svg>
      ),
    } : null,
    panel: open && ready
      ? <JournalPanel key={key} groups={groups} live={busy} continuation={ctx.continuation} onAsk={ctx.canRequest ? (step, name) => ctx.insertRequest(askAbout(step, name, t)) : null} loadImage={loadImage} />
      : null,
  };
}
